import React, { useEffect, useState } from 'react';
import { Form, FormInstance, SelectBooleanInput } from 'blueprint-form';

import { CardBlock } from '../../card';
import { LoadingBlock } from '../../loading/progress';
import Modal from '../../modal';
import Toast from '../../toast';
import Callout from '../../callout';

import { Col, Row } from '../../../../grid';
import Button from '../../../molecules/button';
import Icon from '../../../atoms/icon';
import { faSave } from '@fortawesome/pro-solid-svg-icons';
import { ConfigComponent } from '../../../../libs/apps';
import { PostJSON, BaseResponse } from '../../../../libs/request';
import { useTerms } from '../../../../contexts/terms';
import { useGetAccountSettings } from '../../../../libs/account';

interface EpxDiabetesConfigFormData {
  hideConnect: {
    [key: string]: boolean;
  };
}

/**
 * Diabetes Program Configuration Form
 *
 * @returns JSX.Element
 */
export const EpxDiabetesConfigForm: ConfigComponent = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [settings, setSettings] = useState<EpxDiabetesConfigFormData>(null);
  const { terms } = useTerms();
  const { settings: existingSettings, isLoading: isDataLoading } =
    useGetAccountSettings();
  const appName = 'epxdiabetes';
  let initialValue = false;
  if (
    existingSettings &&
    existingSettings?.hideConnect &&
    existingSettings?.hideConnect[appName]
  ) {
    initialValue = existingSettings.hideConnect[appName];
  }
  const initialFormData = {
    hideConnect: {
      [appName]: initialValue
    }
  };
  const form = new FormInstance<EpxDiabetesConfigFormData>(initialFormData);

  // Fill in form with updated settings
  useEffect(() => {
    form.setData(settings);
  }, [settings]);

  /**
   * Save account settings
   */
  const onSubmit = (data: EpxDiabetesConfigFormData) => {
    const params = {
      hideConnect: {
        [appName]: data.hideConnect[appName]
      }
    };
    setIsLoading(true);
    PostJSON<BaseResponse>({
      url: '/user/edit_account_settings/',
      params
    }).then((response) => {
      setIsLoading(false);
      if (response.status !== 'OK') {
        Modal.error({
          title: 'Error Saving Settings',
          content: response.message
        });
        return;
      }
      Toast({
        intent: 'success',
        message: 'The settings have been saved successfully!'
      });
      setSettings(data);
    });
  };

  if (isLoading) {
    return <LoadingBlock />;
  }

  return isDataLoading || isLoading ? null : (
    <Form form={form} onSubmit={onSubmit}>
      <>
        <CardBlock>
          <Row>
            <Col sm={24}>
              <SelectBooleanInput
                label="Hide Connect"
                name={`hideConnect[${appName}]`}
                fill
              />
              <Callout>
                Turning this on will edit the messaging sent to{' '}
                {terms.patient.plural}, so that there are less opportunities for{' '}
                {terms.patient.plural} to connect with {terms.provider.plural}{' '}
                and potentially reduce call volumes for this program.
              </Callout>
            </Col>
          </Row>
          <Row className="mt-7">
            <Col sm={8}>
              <Button
                type="submit"
                icon={<Icon icon={faSave} />}
                intent="primary"
                fill
              >
                Save
              </Button>
            </Col>
          </Row>
        </CardBlock>
      </>
    </Form>
  );
};
